/**
 * CRM > Wholesale Scorecard. The live replacement for the weekly wholesale
 * workbook. KPI cards (weighted pipeline, won this quarter, open count), a
 * weighted-pipeline-by-month bar chart (simple CSS bars, no chart library),
 * weighted pipeline by rep, and a needs-attention list.
 *
 * All data from GET /v1/crm/scorecard.
 */

import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  formatCrmMoney,
  hasCrmScorecard,
  useCrmScorecard,
  type ScorecardByMonth,
  type ScorecardByRep,
  type ScorecardAttention,
} from '@/lib/useCrm';

// Format a YYYY-MM month key as "Jul 2026".
function monthLabel(m: string): string {
  const [y, mo] = m.split('-');
  if (!y || !mo) return m;
  const d = new Date(Date.UTC(Number(y), Number(mo) - 1, 1));
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d);
}

export function CrmScorecard() {
  const { user } = useSession();
  const canRead = hasCrmScorecard(user?.permissions, user?.superAdmin);
  const scorecardQ = useCrmScorecard();

  if (user && !canRead) return <AccessDeniedCard />;

  const data = scorecardQ.data;

  // Total weighted pipeline + open count across the by-month + by-rep rollups.
  const totalWeighted = (data?.weighted_pipeline_by_rep ?? []).reduce(
    (sum, r) => sum + Number(r.weighted_amount ?? 0),
    0,
  );
  const totalOpen = (data?.weighted_pipeline_by_rep ?? []).reduce(
    (sum, r) => sum + Number(r.open_opp_count ?? 0),
    0,
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">CRM</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Wholesale Scorecard</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Weighted pipeline, wins this fiscal quarter, and what needs attention.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="scorecard" toolName="Wholesale Scorecard" />
        </div>
      </div>

      {scorecardQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading scorecard...</p>
      ) : scorecardQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {scorecardQ.error.message}</p>
      ) : data ? (
        <div className="min-h-0 flex-1 overflow-auto space-y-6 pb-4">
          {/* KPI cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard label="Weighted pipeline" value={formatCrmMoney(totalWeighted)} sub="Open opportunities, probability-weighted" />
            <KpiCard
              label="Won this quarter"
              value={formatCrmMoney(data.won_this_quarter.won_amount)}
              sub={`${data.won_this_quarter.won_count} deal${data.won_this_quarter.won_count === 1 ? '' : 's'} closed won`}
            />
            <KpiCard label="Open opportunities" value={String(totalOpen)} sub="Currently open across all reps" />
          </div>

          <ByMonthCard rows={data.weighted_pipeline_by_month} />
          <ByRepCard rows={data.weighted_pipeline_by_rep} />
          <NeedsAttentionCard rows={data.needs_attention} />
        </div>
      ) : null}
    </div>
  );
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">{label}</p>
      <p className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{value}</p>
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1">{sub}</p>
    </div>
  );
}

function ByMonthCard({ rows }: { rows: ScorecardByMonth[] }) {
  const max = rows.reduce((m, r) => Math.max(m, Number(r.weighted_amount ?? 0)), 0);
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">Weighted pipeline by expected close month</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No open opportunities with a close date.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const val = Number(r.weighted_amount ?? 0);
            const pct = max > 0 ? Math.max(2, Math.round((val / max) * 100)) : 0;
            return (
              <div key={r.month} className="flex items-center gap-3">
                <span className="w-20 shrink-0 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{monthLabel(r.month)}</span>
                <div className="flex-1 h-5 rounded bg-amp-cream-warm dark:bg-amp-dark-surface-warm overflow-hidden">
                  <div className="h-full rounded bg-amp-olive dark:bg-amp-olive-dark" style={{ width: `${pct}%` }} aria-hidden />
                </div>
                <span className="w-28 shrink-0 text-right text-xs text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">
                  {formatCrmMoney(r.weighted_amount)}
                </span>
                <span className="w-10 shrink-0 text-right text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{r.opp_count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ByRepCard({ rows }: { rows: ScorecardByRep[] }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white overflow-hidden dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="border-b border-amp-line px-4 py-2 dark:border-amp-dark-line">
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">Weighted pipeline by rep</h2>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No open opportunities.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Rep</th>
              <th className="px-4 py-2 text-right font-medium w-[100px]">Open</th>
              <th className="px-4 py-2 text-right font-medium w-[150px]">Weighted</th>
              <th className="px-4 py-2 text-right font-medium w-[150px]">Projected</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {rows.map((r) => (
              <tr key={r.sales_rep_id ?? 'unassigned'} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{r.sales_rep_name ?? 'Unassigned'}</td>
                <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">{r.open_opp_count}</td>
                <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(r.weighted_amount)}</td>
                <td className="px-4 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatCrmMoney(r.projected_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NeedsAttentionCard({ rows }: { rows: ScorecardAttention[] }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white overflow-hidden dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="flex items-center gap-2 border-b border-amp-line px-4 py-2 dark:border-amp-dark-line">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden />
        <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
          Needs attention <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted font-normal">({rows.length})</span>
        </h2>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Nothing needs attention right now.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Opportunity</th>
              <th className="px-4 py-2 text-left font-medium">Account</th>
              <th className="px-4 py-2 text-left font-medium w-[120px]">Reason</th>
              <th className="px-4 py-2 text-left font-medium w-[110px]">Close</th>
              <th className="px-4 py-2 text-right font-medium w-[130px]">Weighted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                  <Link to={`/crm/opportunities/${r.id}`} className="hover:underline">{r.title}</Link>
                </td>
                <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{r.account_name ?? '--'}</td>
                <td className="px-4 py-2">
                  <Pill tone={r.reason === 'past_close' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}>
                    {r.reason === 'past_close' ? 'Past close' : 'Stale'}
                  </Pill>
                </td>
                <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(r.expected_close_date)}</td>
                <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(r.weighted_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        CRM is gated by the crm:read capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
